import { type BootstrapResponse, ErrorCode, type Guild, type Resources } from '@guildrise/shared';
import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import {
  ApiError,
  createGuild,
  fetchBootstrap,
  getAuthRevision,
  subscribeAuthSession,
  uploadLogo,
} from '../../shared/api.js';
import type { GuildDraft } from './CreateGuildWizard.js';

/**
 * Состояние первого запуска.
 *
 * Шаг хранится на сервере, а не в клиенте: закрытое приложение и смена
 * устройства не должны терять прогресс.
 */

export type GuildState =
  | { status: 'loading' }
  | { status: 'error'; message: string; code: string }
  | {
      status: 'create';
      errorMessage: string | null;
      busy: boolean;
      /**
       * Пару «ник#номер» заняли до создания. undefined — такого не было;
       * null — заняты все номера; строка — свободный номер для подсказки.
       */
      takenSuggestion?: string | null;
    }
  | { status: 'ready'; guild: Guild; resources: Resources };

export function useGuild(enabled: boolean) {
  const revision = useSyncExternalStore(subscribeAuthSession, getAuthRevision, getAuthRevision);
  const [loaded, setLoaded] = useState<{ revision: number; state: GuildState }>(() => ({
    revision,
    state: { status: 'loading' },
  }));
  const setState = useCallback(
    (state: GuildState) => {
      setLoaded({ revision, state });
    },
    [revision],
  );
  const isCurrentSession = useCallback(
    () => enabled && revision === getAuthRevision(),
    [enabled, revision],
  );

  /**
   * Ключ идемпотентности живёт столько же, сколько попытка создания.
   * Новый ключ на каждый повтор создал бы вторую гильдию.
   */
  const createKeyRef = useRef({ revision, key: crypto.randomUUID() });
  const runIdRef = useRef(0);

  const load = useCallback(async () => {
    if (!isCurrentSession()) return;
    const runId = ++runIdRef.current;
    setState({ status: 'loading' });

    try {
      const data: BootstrapResponse = await fetchBootstrap();
      if (!isCurrentSession() || runId !== runIdRef.current) return;

      setState(
        data.guild === null || data.resources === null
          ? { status: 'create', errorMessage: null, busy: false }
          : { status: 'ready', guild: data.guild, resources: data.resources },
      );
    } catch (error) {
      if (!isCurrentSession() || runId !== runIdRef.current) return;
      const api = error instanceof ApiError ? error : null;
      setState({
        status: 'error',
        message: api?.message ?? 'Сервер недоступен',
        code: api?.code ?? 'INTERNAL',
      });
    }
  }, [isCurrentSession, setState]);

  useEffect(() => {
    if (enabled) void load();
    return () => {
      runIdRef.current += 1;
    };
  }, [enabled, load]);

  const submit = useCallback(
    async (input: GuildDraft) => {
      if (!isCurrentSession()) return;
      const runId = ++runIdRef.current;
      if (createKeyRef.current.revision !== revision) {
        createKeyRef.current = { revision, key: crypto.randomUUID() };
      }
      setState({ status: 'create', errorMessage: null, busy: true });

      try {
        await createGuild(input, createKeyRef.current.key);
        if (!isCurrentSession() || runId !== runIdRef.current) return;
        // Перечитываем созданную гильдию и ресурсы с сервера.
        void load();
      } catch (error) {
        if (!isCurrentSession() || runId !== runIdRef.current) return;
        const api = error instanceof ApiError ? error : null;

        if (api?.code === ErrorCode.CLOSED_ACCESS_REQUIRED) {
          setState({ status: 'error', code: api.code, message: api.message });
          return;
        }

        // Гильдия уже есть — значит, предыдущая попытка дошла.
        // Перечитываем состояние вместо показа ошибки.
        if (api?.code === ErrorCode.GUILD_ALREADY_EXISTS) {
          void load();
          return;
        }

        // Пару «ник#номер» заняли, пока игрок шёл по мастеру. Это другой
        // запрос по смыслу — следующая попытка с новым номером получает
        // новый ключ, иначе сервер вернул бы запомненный отказ.
        if (api?.code === ErrorCode.NAME_TAKEN) {
          createKeyRef.current = { revision, key: crypto.randomUUID() };
          const suggestion = api.details?.suggestion;
          setState({
            status: 'create',
            busy: false,
            errorMessage: null,
            takenSuggestion: typeof suggestion === 'string' ? suggestion : null,
          });
          return;
        }

        setState({
          status: 'create',
          busy: false,
          errorMessage: api?.message ?? 'Не удалось создать гильдию',
        });
      }
    },
    [load, isCurrentSession, revision, setState],
  );

  /** Загрузка логотипа. Свой ключ: это отдельная операция. */
  const upload = useCallback(
    async (blob: Blob): Promise<string> => {
      const runId = runIdRef.current;
      const assertCurrent = () => {
        if (!isCurrentSession() || runId !== runIdRef.current) {
          throw new DOMException('Сессия изменилась', 'AbortError');
        }
      };
      assertCurrent();
      try {
        const result = await uploadLogo(blob, crypto.randomUUID());
        assertCurrent();
        return result.logoKey;
      } catch (error) {
        assertCurrent();
        if (error instanceof ApiError && error.code === ErrorCode.CLOSED_ACCESS_REQUIRED) {
          setState({ status: 'error', code: error.code, message: error.message });
        }
        throw error;
      }
    },
    [isCurrentSession, setState],
  );

  // Hide private state in the render itself, before effects start the new bootstrap.
  const state: GuildState =
    enabled && loaded.revision === revision ? loaded.state : { status: 'loading' };
  return { state, submit, upload, retry: load };
}
